import { isString, isObject } from '@/types/typeGuards';
import {
    FetchErrorMessage,
    LegacyFetchErrorFormatSchema,
    NewFetchErrorFormatSchema,
    LegacyFetchErrorFormat,
    NewFetchErrorFormat,
} from '@/types/types/general/errors';
import { logger } from '@/utils/logger/logger';

type PossibleErrorFormats = string | LegacyFetchErrorFormat | NewFetchErrorFormat;

const isLegacyFetchError = (input: FetchErrorMessage): input is LegacyFetchErrorFormat =>
    LegacyFetchErrorFormatSchema.safeParse(input).success;

const isNewFetchErrorFormat = (input: FetchErrorMessage): input is NewFetchErrorFormat =>
    NewFetchErrorFormatSchema.safeParse(input).success;

const fetchErrorMessageToString = (error: FetchErrorMessage): string => {
    if (isString(error)) {
        return error;
    }

    if (isLegacyFetchError(error)) {
        if (typeof error.message === 'string') {
            return error.message;
        } else {
            return Array.isArray(error.message.message)
                ? error.message.message.join(', ')
                : error.message.message;
        }
    }

    if (isNewFetchErrorFormat(error)) {
        return error.errors
            .map((error) => (typeof error === 'object' ? error.message : ''))
            .join(', ');
    }

    logger.error('Unknown error format: ', error);
    return '';
};

export class FetchError extends Error {
    readonly status: number;
    readonly info: string;
    readonly phraseId: number | undefined;
    readonly phrasePlaceholders: (string | number)[] | undefined;
    readonly errors: NewFetchErrorFormat['errors'] | undefined;

    constructor(error: PossibleErrorFormats, res: Response) {
        super(fetchErrorMessageToString(error));
        if (isLegacyFetchError(error) && isObject(error.message)) {
            this.phraseId = error.message.phraseId;
            this.phrasePlaceholders = error.message.phrasePlaceholders
                ? error.message.phrasePlaceholders.map((p) => String(p))
                : [];
        } else if (isNewFetchErrorFormat(error)) {
            this.errors = error.errors;
        }
        this.name = 'FetchError';
        this.status = res.status;
        this.info = JSON.stringify({
            response: {
                headers: res.headers,
                status: res.status,
                statusText: res.statusText,
                url: res.url,
                type: res.type,
                body: res.body,
                ok: res.ok,
                originalResponse: isObject(res) ? JSON.stringify(res) : res,
            },
        });
    }
}
